/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {deleteLocalStorage} from '@ion/core/src/capabilities/storage';
import {error as logger} from '@ion/core/src/logger/logger';
import {setConnectErrorHandler} from '@ion/core/src/services/ConnectUtils';
import {ServiceError} from '@ion/core/src/types/services';
import {getRoute, Route} from '@/routes/RouteDefinitions';
import {ApplicationKeys} from '@/types/application';
import {toggleUserPrompt, UserPromptOptions} from './user-prompt';

export const SERVICE_ERROR         = 'SERVICE_ERROR';
export const SERVICE_ERROR_RETRY   = 'SERVICE_ERROR_RETRY';
export const SERVICE_ERROR_DISMISS = 'SERVICE_ERROR_DISMISS';

/**
 * Provides the initialization hook for adding global error interceptors.
 */
export const addErrorInterceptor = dispatch => {
  const interceptor = (error: ServiceError) => {
    dispatch(serviceError(error));
    logger(`CTC Admin Service Error [${error.status || 'N/A'}]`);
    return Promise.reject(error);
  };
  setConnectErrorHandler(interceptor);
};

/**
 * Dispatched when a global service error occurs in iOn, at which point the
 * error reducer state is updated and the user is notified of the error.
 */
export const serviceError = (error?: ServiceError) => {
  return {type: SERVICE_ERROR, error};
};

/**
 * Dispatched when a user chooses to retry a the operation which causes the global
 * service error to occur.
 */
export const retry = () => {
  return {type: SERVICE_ERROR_RETRY};
};

/**
 * Dispatched when the user chooses to dismiss a global service error notification.
 */
export const dismiss = () => {
  return {type: SERVICE_ERROR_DISMISS};
};

/**
 * Dispatched when the user chooses to log back in after being notified that their
 * session has expired.
 */
export const restart = () => {
  return async() => {
    deleteLocalStorage(ApplicationKeys.CTCAdminSession);
    location.assign(getRoute(Route.LOGIN));
  };
};

/**
 * The `onFailurePrompt` action is to be dispatched when a given feature fails in
 * some way (typically, when a service required by the feature fails) and the user
 * should be informed of the failure.
 *
 * When dispatched, this action will in turn dispatch a `toggleUserPrompt` action
 * with a set of pre-defined values which will subsequently be displayed to the User
 * via the `GlobalUserPrompt`.
 *
 * The pre-defined values are as follows:
 *   - message     : 'notifications:error.server'
 *   - actionLabel : 'notifications:error.refresh'
 *   - autoHide    : false
 *
 * Clients can also provide a specific `UserPromptOptions` argument to customize the
 * `GlobalUserPrompt` as needed.
 *
 * As noted, this action creator delegates to dispatch a `toggleUserPrompt` action,
 * which in turn sets the `user-prompt` state to the  provided `UserPromptOptions`
 * (if any).
 *
 * ```typescript
 *  import {onFailurePrompt} from '@/store/actions/errors';
 *  ...
 *
 *  try {
 *    const value = await someService();
 *  } catch (err) {
 *    if ((err as ServiceError)?.errorCode) {
 *      dispatch(onFailurePrompt(...));
 *    }
 *  }
 *
 * ```
 *
 * @param options An optional `UserPromptOptions` argument which, when provided,
 *        allows for overriding the default options, or augmenting them with
 *        additional values as needed.
 */
export const onFailurePrompt = (options?: Partial<UserPromptOptions>) => {
  return dispatch => {
    dispatch(toggleUserPrompt({
      message     : 'notifications:error.server',
      actionLabel : 'notifications:error.refresh',
      autoHide    : false,
      ...(options || {})
    }));
  };
};
